//#region Imports
import { useEffect, useState } from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { Text, Platform, SafeAreaView, View, StyleSheet, useWindowDimensions } from 'react-native';
import { useColorScheme } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@react-navigation/native';
import { useAuthContext } from '../hooks/useAuthContext';
import HomePageHRAdmin from '../screens/HR/HRAdminShortcutsPage/HomePageHRAdmin';
import HRAdminPageCompany from '../screens/HR/HRAdminShortcutsPage/HRAdminPageCompany';
import HRAdminPageReports from '../screens/HR/HRAdminShortcutsPage/HRAdminPageReports';
import HRAdminPageClients from '../screens/HR/HRAdminShortcutsPage/HRAdminPageClients';
import HRAdminPageUtilities from '../screens/HR/HRAdminShortcutsPage/HRAdminPageUtilities';
import HRAdminPageEmployees from '../screens/HR/HRAdminShortcutsPage/HRAdminPageEmployees';
import HRAdminPageTrainings from '../screens/HR/HRAdminShortcutsPage/HRAdminPageTrainings';
import HRAdminPageAnalytics from '../screens/HR/HRAdminShortcutsPage/HRAdminPageAnalytics';
import CustomLabel from '../components/CustomLabel/CustomLabel';
import { useNavigation } from '@react-navigation/native';
import { useAccount } from '../hooks/useAccount';
import { HRAdminCompanyContextProvider } from '../context/HRAdminCompanyContext';
import withRoleGuard from '../components/withRoleGuard';
import useRole from '../hooks/userRole';
import { userRights } from '../hooks/useUserRights';
import { TASKS, TASK_OPTIONS } from '../constants/Tasks';
//#endregion

const Tab = createMaterialTopTabNavigator();

const TopTabNavigationAdmin = ({ drawer, }) => {

    //#region Definitions
    const navigation = useNavigation();
    const { width, height } = useWindowDimensions();
    const { hasAccess, hasOptionAccess, isAdmin } = useRole();
    const { getVisibleScreens } = userRights();
    const { t } = useTranslation();
    const { colors } = useTheme()
    const { user } = useAuthContext();
    const { accountGet } = useAccount();
    //! Forcing Light Mode for now
    let colorScheme = useColorScheme();
    colorScheme = 'light';
    // const screenChecks = ['Admin', 'Leaves', 'LeaveApprover', 'Survey', 'Assessments'];
    const [permissions, setPermissions] = useState({});
    const [customWidth, setCustomWidth] = useState(width);
    const styles = makeStyles(colors, width, user, permissions, drawer);
    //#endregion

    //#region RefreshWeb
    //* if refreshed, navigate to last visited page
    const checkIfRefreshed = async () => {
        try {
            let isRefresh = localStorage.getItem('isRefresh');
            if (isRefresh === "true") {
                let lastVisitedScreen = localStorage.getItem('lastVisitedScreen');
                // console.log(lastVisitedScreen)
                switch (lastVisitedScreen) {
                    case "HomePageHRAdmin":
                        navigation.navigate(drawer, { screen: 'HomePageHRAdmin' });
                        break;
                    case "HRAdminPageCompany":
                        navigation.navigate(drawer, { screen: 'HRAdminPageCompany', params: { permissions } });
                        break;
                    case "HRAdminPageEmployees":
                        navigation.navigate(drawer, { screen: 'HRAdminPageEmployees' });
                        break;
                    case "HRAdminPageReports":
                        navigation.navigate(drawer, { screen: 'HRAdminPageReports' });
                        break;
                    case "HRAdminPageClients":
                        navigation.navigate(drawer, { screen: 'HRAdminPageClients' });
                        break;
                    case "HRAdminPageUtilities":
                        navigation.navigate(drawer, { screen: 'HRAdminPageUtilities' });
                        break;
                    case "HRAdminPageAnalytics":
                        navigation.navigate(drawer, { screen: 'HRAdminPageAnalytics' });
                        break;

                    //   default:
                    //     navigation.navigate(lastVisitedScreen);
                    //     break;
                };
            };
        } catch (error) {
            console.log(error);
        };
    };

    const accGet = async () => {
        await accountGet().catch(console.error);
    }

    //#endregion

    //#region UseEffects 
    useEffect(
        () => {
            // * No need to do anything here
        },
        [width, height]
    );

    useEffect(() => {

        accGet().then(() => { checkIfRefreshed(); })
    }, [])

    useEffect(() => {
        getVisibleScreens().catch();
    }, []);
    //#endregion
    // * if permissions is not loaded show loading screen 
    if (typeof permissions === 'object' && !Array.isArray(permissions) && permissions === null) {
        return (
            <SafeAreaView
                style={styles.main}>
                <CustomLabel
                    value={t("common:loading")}
                    type="ASSM"
                />
            </SafeAreaView>
        )
    }

    // * Load the top tab navigation for employee or HR administration which is selected from the Drawer Navigation 
    return (
        <SafeAreaView style={{ flex: 1 }}>
            <View style={{ flex: 1, flexDirection: 'column' }}>

                <HRAdminCompanyContextProvider>
                    <Tab.Navigator
                        initialRouteName={'HRAdminPageCompany'}
                        backBehavior="order"
                        screenOptions={({ route }) => ({
                            headerShown: false,
                            lazy: true,
                            tabBarScrollEnabled: true,
                            animationEnabled: Platform.OS === 'web' ? false : true,
                            tabBarItemStyle: {
                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                            },
                            tabBarIndicatorStyle: {
                                backgroundColor: colors.top_tab_nav_underline,
                                height: 3,
                            },
                            tabBarStyle: {
                                borderTopWidth: 0,
                                borderTopColor: 'transparent',
                                elevation: 0,
                                shadowOpacity: 0,

                                backgroundColor: colorScheme === 'light' ? '#FFFFFF' : '#000000',
                            },
                            swipeEnabled: false, //! disable swiping for now, since only one tab
                        })}>

                        {<Tab.Screen name="HRAdminPageCompany"
                            component={HRAdminPageCompany}
                            lazy={false}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:hrAdminCompany')}</Text>),
                                title: t('navigate:hrAdminCompany'),
                            }}
                        />}
                        {(hasAccess(TASKS.EMPLOYEE, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageEmployees"
                                component={HRAdminPageEmployees}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrAdminEmployees')}</Text>),
                                    title: t('navigate:hrAdminEmployees'),
                                }}
                            />}
                        {(hasAccess(TASKS.TRAINING, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageTrainings"
                                component={HRAdminPageTrainings}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrAdminTrainings')}</Text>),
                                    title: t('navigate:hrAdminTrainings'),
                                }}
                            />}
                        
                        {(hasAccess(TASKS.REPORTS, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageReports"
                                component={HRAdminPageReports}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrAdminReports')}</Text>),
                                    title: t('navigate:hrAdminReports'),
                                }}
                                initialParams={{ permissions }}
                            />}
                        {(hasAccess(TASKS.ADMINTIME, 'hr') || isAdmin) && <Tab.Screen
                            name="HomePageHRAdmin"
                            component={HomePageHRAdmin}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:hrAdmin')}</Text>),
                                title: t('navigate:hrAdmin'),
                            }}
                        />}
                        {(hasOptionAccess(TASKS.EMPLOYEE, TASK_OPTIONS.CLIENTSERVICE, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageClients"
                                component={HRAdminPageClients}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrAdminClients')}</Text>),
                                    title: t('navigate:hrAdminClients'),
                                }}
                            />
                        }
                        {(hasAccess(TASKS.UTILS, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageUtilities"
                                component={HRAdminPageUtilities}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrUtilities')}</Text>),
                                    title: t('navigate:hrUtilities'),
                                }}
                            />
                        }
                        {
                            /* invastiage has access

                            */
                        }
                        {(hasAccess(TASKS.ANALYTICS, 'hr') || isAdmin) &&
                            <Tab.Screen name="HRAdminPageAnalytics"
                                component={HRAdminPageAnalytics}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:hrAdminAnalytics')}</Text>),
                                    title: t('navigate:hrAdminAnalytics'),
                                }}
                            />
                        }


                    </Tab.Navigator >
                </HRAdminCompanyContextProvider>
            </View>

        </SafeAreaView >

    );
};

const makeStyles = (colors, width, user, permissions, drawer, customWidth) => StyleSheet.create({

    margin: {
        alignContent: 'right',
    }
})
export default TopTabNavigationAdmin;